import React from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";

function StepCard({ idx, title, status, body, ctaTo, ctaLabel, done }) {
  const stateClass = done
    ? "border-success"
    : status === "locked"
    ? "border-line opacity-60"
    : "border-oxblood";
  return (
    <div className={`border-l-4 ${stateClass} border-y border-r border-line bg-parchment p-6`}
         data-testid={`supplier-step-${idx}`}>
      <div className="flex items-baseline justify-between gap-4">
        <div className="overline">STEP 0{idx}</div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-sub">
          {done ? "complete" : status === "locked" ? "locked" : "action required"}
        </div>
      </div>
      <h3 className="h-serif text-2xl text-ink mt-2">{title}</h3>
      <p className="text-sub text-sm mt-3 leading-relaxed">{body}</p>
      {ctaTo && status !== "locked" && !done && (
        <Link to={ctaTo} className="btn-primary text-sm mt-5 inline-flex" data-testid={`supplier-step-${idx}-cta`}>
          {ctaLabel} →
        </Link>
      )}
    </div>
  );
}

export default function SupplierDashboard() {
  const { user } = useAuth();
  const approved = user?.status === "approved";
  const kycCleared = (user?.compliance?.kyc_status || "") === "cleared";
  const profileDone = !!user?.supplier_profile?.submitted_at;
  const agreementsDone = !!user?.supplier_agreements?.ncnda_signed_at;

  return (
    <DashboardLayout testid="supplier-dashboard">
      <div className="p-8 md:p-12 max-w-5xl">
        <div className="overline">Supplier desk</div>
        <h1 className="h-serif text-5xl text-ink mt-3">Welcome, {user?.name?.split(" ")[0]}.</h1>
        <p className="text-sub mt-4 max-w-2xl leading-relaxed">
          Four-step onboarding. Each step unlocks the next. Once everything below
          is green, you&apos;re a vetted supplier on the platform — sanitized buyer
          requests routed to you start appearing here in the next iteration.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <StepCard idx={1}
            title="Account approval"
            status={approved ? "done" : "open"}
            done={approved}
            body={approved
              ? "Approved by the brokerage desk."
              : "Your account is in the intake queue. An analyst will review within 24 business hours."}
          />
          <StepCard idx={2}
            title="KYC + Sanctions screening"
            status={!approved ? "locked" : kycCleared ? "done" : "open"}
            done={kycCleared}
            ctaTo="/dashboard/compliance" ctaLabel="Complete KYC profile"
            body={kycCleared
              ? "Corporate profile submitted and cleared against OFAC / EU / UN / UK and PEP lists."
              : "Submit your corporate KYC profile (legal entity, jurisdiction, beneficial owners). We screen everything via OpenSanctions."}
          />
          <StepCard idx={3}
            title="Trade profile"
            status={!kycCleared ? "locked" : profileDone ? "done" : "open"}
            done={profileDone}
            ctaTo="/dashboard/supplier-profile" ctaLabel="Submit trade profile"
            body={profileDone
              ? "Trade profile on file. You may edit it any time."
              : "Tell us what you supply: commodities, origins, monthly capacity, quality specs, payment terms, prior cargoes, banking partner."}
          />
          <StepCard idx={4}
            title="Sign supplier agreements"
            status={!profileDone ? "locked" : agreementsDone ? "done" : "open"}
            done={agreementsDone}
            ctaTo="/dashboard/supplier-agreements" ctaLabel="Review &amp; sign"
            body={agreementsDone
              ? "Master NCNDA, commission agreement, AML attestation and code of conduct on file."
              : "Sign four standing agreements once. They apply to every deal you&apos;re shown."}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}
